import { useParams } from "react-router-dom";

import { useQueryClient } from "@tanstack/react-query";

import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { Controller, useForm } from "react-hook-form";

import { useFetchSessionNote } from "../../../../hooks/queries/note";
import {
    useAddNewAddendum,
    useEditDraftNote,
} from "../../../../hooks/mutations/note";
import useToast from "../../../../hooks/useToast";

import { useFetchUserDetails } from "../../../../hooks/queries/user";
import { truncateString } from "../../../../utils/helpers";

import { INewAddendum, NoteStatus } from "../../types/notes.types";
import DiagnosisCodeSelect from "../DiagnosisCodeSelect/DiagnosisCodeSelect";

interface EditNoteDiagnosisCodesFormProps {
    onFormSubmit(): void;
}

interface EditDiagnosisCodesPayload {
    diagnosis_codes: string;
    secondary_diagnosis: string;
    tertiary_diagnosis: string;
    additional_diagnosis_one: string;
    additional_diagnosis_two: string;
}

interface Option {
    value: string;
    label: string;
}

export default function EditNoteDiagnosisCodesForm({
    onFormSubmit,
}: EditNoteDiagnosisCodesFormProps) {
    const params = useParams();
    const noteId = params.noteId as string;
    const clientId = params.clientId as string;

    const loggedInUser = useFetchUserDetails();

    const isSmallScreen = window.matchMedia("(max-width: 500px)").matches;

    const { data } = useFetchSessionNote(
        clientId,
        noteId,
        Boolean(clientId) && Boolean(noteId)
    );

    const addAddendum = useAddNewAddendum(noteId);

    const {
        handleSubmit,
        control,
        formState: { errors, defaultValues },
    } = useForm<EditDiagnosisCodesPayload>({
        resolver: yupResolver(
            yup.object({
                diagnosis_codes: yup.string().required("Diagnosis is required"),
                secondary_diagnosis: yup.string().nullable(),
                tertiary_diagnosis: yup.string().nullable(),
                additional_diagnosis_one: yup.string().nullable(),
                additional_diagnosis_two: yup.string().nullable(),
            })
        ),
        mode: "onChange",
        defaultValues: data?.data.note_content?.diagnosis_codes
            ? {
                  diagnosis_codes:
                      typeof data?.data.note_content?.diagnosis_codes ===
                      "string"
                          ? data?.data.note_content?.diagnosis_codes
                          : data?.data.note_content?.diagnosis_codes[0],
                  secondary_diagnosis:
                      data?.data.note_content?.secondary_diagnosis || null,
                  tertiary_diagnosis:
                      data?.data.note_content?.tertiary_diagnosis || null,
                  additional_diagnosis_one:
                      data?.data.note_content?.additional_diagnosis_one || null,
                  additional_diagnosis_two:
                      data?.data.note_content?.additional_diagnosis_two || null,
              }
            : undefined,
    });

    const editNote = useEditDraftNote();

    const { toast } = useToast();

    const queryClient = useQueryClient();

    const onSubmit = (payload: EditDiagnosisCodesPayload) => {
        const dataToSend = {
            noteId: data?.data.note_id || "",
            data: {
                note_content: {
                    ...data?.data.note_content,
                    diagnosis_codes: [payload.diagnosis_codes],
                    secondary_diagnosis: payload.secondary_diagnosis,
                    tertiary_diagnosis: payload.tertiary_diagnosis,
                    additional_diagnosis_one: payload.additional_diagnosis_one,
                    additional_diagnosis_two: payload.additional_diagnosis_two,
                },
            },
        };

        let addendumData: INewAddendum = [];

        const getDefaultDiagnosisCode = () => {
            if (!defaultValues?.diagnosis_codes) {
                return null;
            }
            return typeof defaultValues?.diagnosis_codes === "string"
                ? defaultValues?.diagnosis_codes
                : defaultValues?.diagnosis_codes[0];
        };

        if (
            (!getDefaultDiagnosisCode() && payload.diagnosis_codes) ||
            getDefaultDiagnosisCode() !== payload.diagnosis_codes
        ) {
            addendumData = addendumData.concat({
                field_id: "diagnosis_codes",
                field_title: "Primary diagnosis",
                title: "Updating primary diagnosis",
                signature: `${loggedInUser?.data?.first_name} ${loggedInUser?.data?.last_name}`,
                signature_datetime: new Date(Date.now()).toISOString(),
                content: `Primary diagnosis code updated from ${
                    getDefaultDiagnosisCode() ?? "--"
                } to ${payload.diagnosis_codes}`,
            });
        }

        if (
            (!defaultValues?.secondary_diagnosis &&
                payload.secondary_diagnosis) ||
            defaultValues?.secondary_diagnosis !== payload.secondary_diagnosis
        ) {
            addendumData = addendumData.concat({
                field_id: "secondary_diagnosis",
                field_title: "Secondary diagnosis",
                title: "Updating secondary diagnosis",
                signature: `${loggedInUser?.data?.first_name} ${loggedInUser?.data?.last_name}`,
                signature_datetime: new Date(Date.now()).toISOString(),
                content:
                    defaultValues?.secondary_diagnosis &&
                    payload.secondary_diagnosis
                        ? `Secondary diagnosis code updated from ${defaultValues.secondary_diagnosis} to ${payload.secondary_diagnosis}`
                        : `Secondary diagnosis code ${
                              payload.secondary_diagnosis
                                  ? `updated to ${payload.secondary_diagnosis}`
                                  : `removed`
                          } `,
            });
        }

        if (
            (!defaultValues?.tertiary_diagnosis &&
                payload.tertiary_diagnosis) ||
            defaultValues?.tertiary_diagnosis !== payload.tertiary_diagnosis
        ) {
            addendumData = addendumData.concat({
                field_id: "tertiary_diagnosis",
                field_title: "Tertiary diagnosis",
                title: "Updating tertiary diagnosis",
                signature: `${loggedInUser?.data?.first_name} ${loggedInUser?.data?.last_name}`,
                signature_datetime: new Date(Date.now()).toISOString(),
                content:
                    defaultValues?.tertiary_diagnosis &&
                    payload.tertiary_diagnosis
                        ? `Tertiary diagnosis code updated from ${defaultValues.tertiary_diagnosis} to ${payload.tertiary_diagnosis}`
                        : `Tertiary diagnosis code ${
                              payload.tertiary_diagnosis
                                  ? `updated to ${payload.tertiary_diagnosis}`
                                  : `removed`
                          } `,
            });
        }

        if (
            (!defaultValues?.additional_diagnosis_one &&
                payload.additional_diagnosis_one) ||
            defaultValues?.additional_diagnosis_one !==
                payload.additional_diagnosis_one
        ) {
            addendumData = addendumData.concat({
                field_id: "additional_diagnosis_one",
                field_title: "Additional Diagnosis 1",
                title: "Updating additional diagnosis 1",
                signature: `${loggedInUser?.data?.first_name} ${loggedInUser?.data?.last_name}`,
                signature_datetime: new Date(Date.now()).toISOString(),
                content:
                    defaultValues?.tertiary_diagnosis &&
                    payload.tertiary_diagnosis
                        ? `Additional Diagnosis 1 updated from ${defaultValues.additional_diagnosis_one} to ${payload.additional_diagnosis_one}`
                        : `Additional Diagnosis 1 ${
                              payload.additional_diagnosis_one
                                  ? `updated to ${payload.additional_diagnosis_one}`
                                  : `removed`
                          } `,
            });
        }

        if (
            (!defaultValues?.additional_diagnosis_two &&
                payload.additional_diagnosis_two) ||
            defaultValues?.additional_diagnosis_two !==
                payload.additional_diagnosis_two
        ) {
            addendumData = addendumData.concat({
                field_id: "additional_diagnosis_two",
                field_title: "Additional Diagnosis 2",
                title: "Updating additional diagnosis 2",
                signature: `${loggedInUser?.data?.first_name} ${loggedInUser?.data?.last_name}`,
                signature_datetime: new Date(Date.now()).toISOString(),
                content:
                    defaultValues?.tertiary_diagnosis &&
                    payload.tertiary_diagnosis
                        ? `Additional Diagnosis 2 updated from ${defaultValues.additional_diagnosis_two} to ${payload.additional_diagnosis_two}`
                        : `Additional Diagnosis 2 ${
                              payload.additional_diagnosis_two
                                  ? `updated to ${payload.additional_diagnosis_two}`
                                  : `removed`
                          } `,
            });
        }

        editNote.mutate(dataToSend, {
            onSuccess: async (res) => {
                await queryClient.invalidateQueries({
                    queryKey: [clientId, `session-note`, noteId],
                });

                toast({
                    mode: "success",
                    message:
                        res.message ||
                        "Note diagnosis codes edited successfully",
                });

                if (
                    addendumData &&
                    addendumData.length > 0 &&
                    data?.data?.note_status !== NoteStatus.DRAFT
                ) {
                    addAddendum.mutate(addendumData, {
                        onSuccess: () => {
                            toast({
                                mode: "success",
                                message: "Addendum added successfully",
                            });

                            queryClient.invalidateQueries({
                                queryKey: [clientId, "session-note", noteId],
                            });
                            queryClient.invalidateQueries({
                                queryKey: [noteId, "addendum"],
                            });
                            queryClient.invalidateQueries({
                                queryKey: [noteId, `activity`],
                            });
                            onFormSubmit();
                        },
                        onError: (err) => {
                            toast({
                                mode: "error",
                                message:
                                    err.response?.data.message ||
                                    "Could not add addendum at this time",
                            });
                            onFormSubmit();
                        },
                    });
                } else {
                    onFormSubmit();
                }
            },
            onError: (err) => {
                toast({
                    mode: "error",
                    message:
                        err.response?.data.message ||
                        "Could not edit diagnosis codes at this time",
                });
            },
        });
    };

    return (
        <form id="edit-diagnosis-codes" onSubmit={handleSubmit(onSubmit)}>
            <div className="fg">
                <Controller
                    name="diagnosis_codes"
                    control={control}
                    render={({ field }) => (
                        <DiagnosisCodeSelect
                            label="Primary diagnosis"
                            value={field.value}
                            onChange={field.onChange}
                            placeholder="Primary diagnosis"
                            errorText={
                                errors.diagnosis_codes?.type === "typeError"
                                    ? "Diagnosis codes are required"
                                    : errors.diagnosis_codes?.message
                            }
                            closeMenuOnSelect
                            isLongListInDialog
                        />
                    )}
                />
            </div>
            <div className="fg">
                <Controller
                    name="secondary_diagnosis"
                    control={control}
                    render={({ field }) => (
                        <DiagnosisCodeSelect
                            label="Secondary diagnosis"
                            value={field.value}
                            onChange={field.onChange}
                            placeholder="Secondary diagnosis"
                            formatOptionLabel={(selectData, meta) => {
                                return meta.context === "menu"
                                    ? (selectData as Option).label
                                    : truncateString(
                                          (selectData as Option).label,
                                          isSmallScreen ? 20 : 40
                                      );
                            }}
                            errorText={errors.secondary_diagnosis?.message}
                            isLongListInDialog
                            closeMenuOnSelect
                            isClearable
                        />
                    )}
                />
            </div>
            <div className="fg">
                <Controller
                    name="tertiary_diagnosis"
                    control={control}
                    render={({ field }) => (
                        <DiagnosisCodeSelect
                            label="Tertiary diagnosis"
                            value={field.value}
                            onChange={field.onChange}
                            placeholder="Tertiary diagnosis"
                            formatOptionLabel={(selectData, meta) => {
                                return meta.context === "menu"
                                    ? (selectData as Option).label
                                    : truncateString(
                                          (selectData as Option).label,
                                          isSmallScreen ? 20 : 40
                                      );
                            }}
                            errorText={errors.tertiary_diagnosis?.message}
                            isLongListInDialog
                            isClearable
                        />
                    )}
                />
            </div>
            <div className="fg">
                <Controller
                    name="additional_diagnosis_one"
                    control={control}
                    defaultValue={
                        data?.data.note_content?.additional_diagnosis_one
                    }
                    render={({ field }) => (
                        <DiagnosisCodeSelect
                            label="Additional diagnosis 1"
                            value={field.value}
                            onChange={field.onChange}
                            placeholder="Additional diagnosis 1"
                            formatOptionLabel={(selectData, meta) => {
                                return meta.context === "menu"
                                    ? (selectData as Option).label
                                    : truncateString(
                                          (selectData as Option).label,
                                          isSmallScreen ? 20 : 40
                                      );
                            }}
                            errorText={errors.additional_diagnosis_one?.message}
                            isLongListInDialog
                            isClearable
                        />
                    )}
                />
            </div>
            <div>
                <Controller
                    name="additional_diagnosis_two"
                    control={control}
                    defaultValue={
                        data?.data.note_content?.additional_diagnosis_two
                    }
                    render={({ field }) => (
                        <DiagnosisCodeSelect
                            label="Additional diagnosis 2"
                            value={field.value}
                            onChange={field.onChange}
                            placeholder="Additional diagnosis 2"
                            formatOptionLabel={(selectData, meta) => {
                                return meta.context === "menu"
                                    ? (selectData as Option).label
                                    : truncateString(
                                          (selectData as Option).label,
                                          isSmallScreen ? 20 : 40
                                      );
                            }}
                            errorText={errors.additional_diagnosis_two?.message}
                            isLongListInDialog
                            isClearable
                        />
                    )}
                />
            </div>
        </form>
    );
}
